import { useMutation, useQueryClient } from "react-query";
import { Controller, useForm } from "react-hook-form";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from "@mui/material";
import BasicDatePicker from "../../../../../../components/BasicDatePicker";
import moment from "moment";
import { TOpenModalPromocoes } from "..";
import { IDataPromocoes } from "../../../../../../types/api/comercial/promocoes";

export default function DialogCriacaoEditPromocoes({
  open,
  handleClose,
  defaultValue,
}: DialogCriacaoEditPromocoesProps) {

  const queryClient = useQueryClient();

  //Valores iniciais
  const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue])
  const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue.defaultValue : null, [defaultValue])
  //

  const { control, handleSubmit } = useForm<IDataPromocoes>({
    defaultValues: {
      id: valoresIniciais?.id,
      cooperativa_id: valoresIniciais?.cooperativa_id,
      nome: valoresIniciais?.nome || "",
      mensagem: valoresIniciais?.mensagem || "",
      periodo_inicio: valoresIniciais?.periodo_inicio || '',
      periodo_fim: valoresIniciais?.periodo_fim || "",
    },
  });

  //Querys e mutations
  const { mutate: criarPromocao, isLoading: isLoadingRegister } = useMutation({
    mutationKey: "criar-promocao",
    mutationFn: (inputs: IDataPromocoes) =>
      axiosClient.post("comercial/configuracoes/criar-promocao", inputs),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao cadastrar promoção!", {
        variant: "error",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["pesquisar-promocoes"]);
      handleClose();
      enqueueSnackbar("Promoção cadastrada com sucesso!", {
        variant: "success",
      });
    },
  });

  const { mutate: editarPromocao, isLoading: isLoadingUpdate } = useMutation({
    mutationKey: "editar-promocao",
    mutationFn: (inputs: IDataPromocoes) =>
      axiosClient.put(
        `comercial/configuracoes/editar-promocao/${valoresIniciais?.id}`,
        inputs
      ),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao atualizar promoção!", {
        variant: "error",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["pesquisar-promocoes"]);
      handleClose();
      enqueueSnackbar("Promoção atualizada com sucesso!", {
        variant: "success",
      });
    },
  });
  //

  const onSubmit = (data: IDataPromocoes) => {
    const payload = {
      id: data.id,
      cooperativa_id: data.cooperativa_id,
      nome: data.nome,
      mensagem: data.mensagem,
      periodo_inicio: data.periodo_inicio,
      periodo_fim: data.periodo_fim,
    };
    if (isEdit) {
      editarPromocao(payload);
    }
    if (!isEdit) {
      criarPromocao(payload);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          {isEdit ? "Editar" : "Cadastrar"} Promoção
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid size={{xs: 12}}>
                <Controller
                  name="nome"
                  control={control}
                  rules={{ required: "Campo é obrigatório" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      required
                      {...field}
                      label="Nome"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid size={{xs: 6}}>
                <Controller
                  name="periodo_inicio"
                  rules={{ required: "Campo obrigatório" }}
                  control={control}
                  render={({ field, fieldState: { error, invalid } }) => (
                    <BasicDatePicker
                      label="Período início *"
                      blockPastDates
                      value={field.value ? moment(field.value) : null}
                      onChange={(data) => field.onChange(data)}
                      formatString="DD/MM/YYYY"
                      error={invalid}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid size={{xs: 6}}>
                <Controller
                  name="periodo_fim"
                  rules={{ required: "Campo obrigatório" }}
                  control={control}
                  render={({ field, fieldState: { error, invalid } }) => (
                    <BasicDatePicker
                      label="Período fim *"
                      blockPastDates
                      value={field.value ? moment(field.value) : null}
                      onChange={(data) => field.onChange(data)}
                      formatString="DD/MM/YYYY"
                      error={invalid}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid size={{xs: 12, sm: 12}}>
                <Controller
                  name="mensagem"
                  control={control}
                  rules={{ required: "Campo é obrigatório" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      required
                      label="Mensagem"
                      fullWidth
                      multiline
                      rows={3}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button variant="outlined" onClick={handleClose}>
              Cancelar
            </Button>

            <Button
              variant="contained"
              type="submit"
              disabled={isLoadingRegister || isLoadingUpdate}
            >
              {(isLoadingRegister || isLoadingUpdate) && (
                <CircularProgress size={20} sx={{ marginRight: 1 }} />
              )}
              {isEdit ? "Atualizar" : "Cadastrar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

interface DialogCriacaoEditPromocoesProps {
  open: boolean;
  handleClose: () => void;
  defaultValue: TOpenModalPromocoes | null;
}
